import React, { useState } from "react";

import { Button } from "../common/design-system/Button";
import { Input } from "../common/design-system/Input";
import { images } from "../../assets";
import WebScraperTabs from "./WebScraperTabs";
import { DialogFooter } from "../common/design-system/Dialog";
import { Checkbox } from "../common/design-system/Checkbox";
import SuccessState from "../common/SuccessState";
import type { BannerState } from "../common/Banner";
import Banner from "../common/Banner";
import { useCarbon } from "../../context/CarbonContext";
import {
  DEFAULT_CHUNK_SIZE,
  DEFAULT_OVERLAP_SIZE,
  DEFAULT_RECURSION_DEPTH,
  ENV,
  MAX_PAGES_TO_SCRAPE,
} from "../../constants/shared";
import {
  getBaseURL,
  isValidHttpUrl,
  removeHttp,
} from "../../utils/helper-functions";
import Loader from "../common/Loader";

import type { WebScraperIntegration } from "../../typing/shared";
import { IntegrationName, ActionType } from "../../typing/shared";

export default function SitemapTabContent({
  processedIntegration,
  setActiveScreen,
  setActiveTab,
  sitemapEnabled,
}: {
  setActiveTab: (val: string) => void;
  sitemapEnabled: boolean;
  processedIntegration: WebScraperIntegration | undefined;
  setActiveScreen: React.Dispatch<React.SetStateAction<"UPLOAD" | "FILES">>;
}) {
  const [internalStep, setInternalStep] = useState<number>(1);
  const [url, setUrl] = useState<string>("");
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [sitemapUrls, setSitemapUrls] = useState([]);
  const [bannerState, setBannerState] = useState<BannerState>({
    message: null,
  });
  const [urlsLoading, setUrlsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const maxPagesToScrape =
    processedIntegration?.maxPagesToScrape || MAX_PAGES_TO_SCRAPE;

  const {
    accessToken,
    apiURL,
    authenticatedFetch,
    chunkSize,
    embeddingModel,
    environment = ENV.PRODUCTION,
    generateSparseVectors,
    maxItemsPerChunk,
    onError,
    onSuccess,
    overlapSize,
    prependFilenameToChunks,
    tags,
  } = useCarbon();

  const handleFetchSitemapUrls = async () => {
    setSelectedUrls([]);
    setSitemapUrls([]);
    const finalUrl = "https://" + url;

    try {
      if (!isValidHttpUrl(finalUrl)) {
        setBannerState({ message: "Please enter a valid URL", type: "ERROR" });

        return;
      }

      setUrlsLoading(true);
      const response = await authenticatedFetch(
        `${getBaseURL(apiURL, environment)}/process_sitemap?url=${finalUrl}`,
        {
          method: "GET",
          headers: {
            Authorization: `Token ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        const responseData = await response.json();

        setSitemapUrls(responseData.urls);
        setInternalStep(2);
      } else {
        throw new Error("Error fetching sitemap. Please try again.");
      }
    } catch (error) {
      setBannerState({
        type: "ERROR",
        message: "Error fetching sitemap. Please try again.",
      });
    }
    setUrlsLoading(false);
  };

  const submitScrapeRequest = async () => {
    const finalUrl = "https://" + url;

    try {
      if (isSubmitting === true) {
        return;
      }

      setIsSubmitting(true);
      const chunkSizeValue =
        processedIntegration?.chunkSize || chunkSize || DEFAULT_CHUNK_SIZE;
      const overlapSizeValue =
        processedIntegration?.overlapSize ||
        overlapSize ||
        DEFAULT_OVERLAP_SIZE;
      const skipEmbeddingGeneration =
        processedIntegration?.skipEmbeddingGeneration || false;
      const enableAutoSync = processedIntegration?.enableAutoSync ?? false;
      const generateSparseVectorsValue =
        processedIntegration?.generateSparseVectors ??
        generateSparseVectors ??
        false;
      const prependFilenameToChunksValue =
        processedIntegration?.prependFilenameToChunks ??
        prependFilenameToChunks ??
        false;
      const maxItemsPerChunkValue =
        processedIntegration?.maxItemsPerChunk || maxItemsPerChunk || null;
      const embeddingModelValue =
        processedIntegration?.embeddingModel || embeddingModel || null;
      const recursionDepth =
        processedIntegration?.recursionDepth ?? DEFAULT_RECURSION_DEPTH;

      const htmlTagsToSkip = processedIntegration?.htmlTagsToSkip || [];
      const cssClassesToSkip = processedIntegration?.cssClassesToSkip || [];
      const cssSelectorsToSkip = processedIntegration?.cssSelectorsToSkip || [];
      const downloadCssAndMedia =
        processedIntegration?.downloadCssAndMedia || false;
      const generateChunksOnly =
        processedIntegration?.generateChunksOnly || false;

      const urlsToScrape =
        selectedUrls.length == sitemapUrls.length ? [] : selectedUrls;

      const requestObject = {
        url: finalUrl,
        tags: tags,
        recursion_depth: recursionDepth,
        max_pages_to_scrape: maxPagesToScrape,
        chunk_size: chunkSizeValue,
        chunk_overlap: overlapSizeValue,
        skip_embedding_generation: skipEmbeddingGeneration,
        enable_auto_sync: enableAutoSync,
        generate_sparse_vectors: generateSparseVectorsValue,
        prepend_filename_to_chunks: prependFilenameToChunksValue,
        html_tags_to_skip: htmlTagsToSkip,
        css_classes_to_skip: cssClassesToSkip,
        css_selectors_to_skip: cssSelectorsToSkip,
        ...(maxItemsPerChunkValue && {
          max_items_per_chunk: maxItemsPerChunkValue,
        }),
        ...(embeddingModelValue && { embedding_model: embeddingModelValue }),
        urls_to_scrape: urlsToScrape,
        download_css_and_media: downloadCssAndMedia,
        generate_chunks_only: generateChunksOnly,
      };

      const uploadResponse = await authenticatedFetch(
        `${getBaseURL(apiURL, environment)}/scrape_sitemap`,
        {
          method: "POST",
          headers: {
            Authorization: `Token ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestObject),
        },
      );
      const responseData = await uploadResponse.json();

      if (uploadResponse.status === 200) {
        setSelectedUrls([]);
        setSitemapUrls([]);
        setUrl("");
        onSuccess &&
          onSuccess({
            status: 200,
            data: {
              data_source_external_id: null,
              sync_status: null,
              files: [responseData],
            },
            action: ActionType.UPDATE,
            event: ActionType.UPDATE,
            integration: IntegrationName.WEB_SCRAPER,
          });
        setActiveScreen("FILES");
        setInternalStep(3);
        setBannerState({
          message: "Scraping request initiated successfully.",
          type: "SUCCESS",
        });
      }
    } catch (e) {
      setBannerState({
        type: "ERROR",
        message: "Error initiating scraping. Please try again.",
      });
      onError &&
        onError({
          status: 400,
          data: [{ message: "Error initiating scraping. Please try again." }],
          action: ActionType.UPDATE,
          event: ActionType.UPDATE,
          integration: IntegrationName.WEB_SCRAPER,
        });
    }
    setIsSubmitting(false);
  };

  if (internalStep === 3) {
    return (
      <div className="cc-flex-grow cc-p-4 cc-overflow-auto cc-flex cc-flex-col">
        <WebScraperTabs
          activeTab="sitemap"
          setActiveTab={setActiveTab}
          sitemapEnabled={sitemapEnabled}
        />
        <SuccessState
          action={() => setInternalStep(1)}
          heading="Scraping request initiated successfully."
        />
      </div>
    );
  }

  return (
    <>
      <Banner bannerState={bannerState} setBannerState={setBannerState} />
      <div className="cc-flex-grow cc-p-4 cc-overflow-auto cc-flex cc-flex-col">
        <WebScraperTabs
          activeTab="sitemap"
          setActiveTab={setActiveTab}
          sitemapEnabled={sitemapEnabled}
        />
        <div
          className="cc-flex cc-space-x-2 cc-items-center cc-w-full cc-h-10 cc-mb-3"
          key={0}
        >
          <div className="cc-flex cc-flex-1 cc-relative">
            <div>
              <img
                alt="tabler_sitemap"
                className="cc-absolute cc-top-3 cc-left-2 cc-pointer-events-none dark:cc-invert-[1] dark:cc-hue-rotate-180"
                src={images.left_icon}
              />
              <Input
                className="cc-w-[100px_!important] !cc-pl-8 cc-rounded-r-none "
                disabled
                placeholder="Enter URL"
                type="text"
                value="https://"
              />
            </div>
            <div className="cc-w-px cc-shrink-0 cc-bg-outline-med_em dark:cc-bg-dark-input-bg" />
            <Input
              className="cc-rounded-l-none"
              onChange={(e) => setUrl(removeHttp(e.target.value))}
              placeholder="Enter URL"
              type="text"
              value={url}
            />
          </div>
          <Button
            className="cc-font-semibold cc-hidden sm:cc-flex dark:!cc-bg-[#fff] dark:!cc-text-[#000]"
            disabled={url === "" || urlsLoading}
            onClick={async () => handleFetchSitemapUrls()}
            size="md"
            variant="neutral-white"
          >
            Fetch
          </Button>
        </div>
        {internalStep === 2 && (
          <div className="sm:cc-h-[369px] cc-border-t cc-flex-grow cc-border-outline-low_em cc-overflow-y-auto cc-overflow-x-hidden  cc-mx-0 cc-border cc-rounded-xl dark:cc-border-[#FFFFFF1F]">
            <div className="cc-flex cc-justify-between cc-items-center cc-bg-surface-surface_1 sm:cc-flex dark:cc-bg-dark-border-color">
              <div className="cc-px-4 cc-py-2 cc-text-sm cc-text-disabledtext cc-capitalize cc-font-bold dark:cc-text-dark-input-text">
                Fetched URLs
              </div>
              <div className="cc-py-2 cc-text-xs cc-text-disabledtext dark:cc-text-dark-text-white cc-capitalize cc-font-bold cc-text-right cc-mr-4">
                {selectedUrls.length &&
                selectedUrls.length === sitemapUrls.length ? (
                  <button
                    className="cc-text-sm cc-font-semibold cc-h-6 cc-text-outline-danger_high_em cc-items-start cc-text-left"
                    onClick={() => setSelectedUrls([])}
                  >
                    Clear selection
                  </button>
                ) : (
                  <label className="cc-flex cc-gap-2 cc-items-center cc-h-6 cc-text-sm cc-text-[#100C20] cc-font-semibold cc-cursor-pointer dark:!cc-text-[#fff] ">
                    <Checkbox
                      checked={
                        selectedUrls.length
                          ? selectedUrls.length === sitemapUrls.length
                          : false
                      }
                      className="cc-my-0.5"
                      onCheckedChange={() => {
                        setSelectedUrls(sitemapUrls);
                      }}
                    />
                    Select all
                  </label>
                )}
              </div>
            </div>
            {urlsLoading ? (
              <Loader />
            ) : sitemapUrls.length > 0 ? (
              <ul className="cc-px-4 sm:cc-px-0 sm:cc-pb-2">
                {sitemapUrls.map((item, index) => {
                  const isChecked = selectedUrls.indexOf(item) >= 0;

                  return (
                    <SitemapItem
                      isChecked={isChecked}
                      item={item}
                      key={index}
                      onSelect={() => {
                        setSelectedUrls((prev) => {
                          if (isChecked) {
                            return prev.filter((url) => url !== item);
                          } else {
                            return [...prev, item];
                          }
                        });
                      }}
                    />
                  );
                })}
              </ul>
            ) : (
              <div className="cc-py-4 cc-text-center cc-text-disabledtext cc-font-medium cc-text-sm">
                No URLs found
              </div>
            )}
          </div>
        )}
      </div>

      {internalStep === 1 && (
        <DialogFooter className="sm:cc-hidden">
          <Button
            className="cc-w-full"
            disabled={url === ""}
            onClick={() => {
              handleFetchSitemapUrls();
            }}
            size="lg"
            variant="primary"
          >
            Fetch
          </Button>
        </DialogFooter>
      )}
      {internalStep === 2 && selectedUrls.length > 0 && (
        <DialogFooter>
          <div className="cc-mb-4 cc-full cc-text-sm cc-flex cc-justify-center cc-text-low_em cc-font-semibold dark:cc-text-dark-text-white">
            <img
              alt="info_fill"
              className="cc-h-5 cc-w-5 cc-flex cc-mr-2 dark:cc-invert-[1] dark:cc-hue-rotate-180"
              src={images.info_fill}
            />
            {selectedUrls.length == sitemapUrls.length
              ? "All URLs selected."
              : `${selectedUrls.length} URL(s) selected.`}
            {selectedUrls.length > maxPagesToScrape &&
              ` First ${maxPagesToScrape} URLs will be synced.`}
          </div>
          <Button
            className="cc-w-full"
            disabled={isSubmitting}
            onClick={() => {
              submitScrapeRequest();
            }}
            size="md"
          >
            Submit
          </Button>
        </DialogFooter>
      )}
    </>
  );
}

interface SitemapItemProps {
  isChecked: boolean;
  onSelect: () => void;
  item: string;
}

function SitemapItem({ isChecked, item, onSelect }: SitemapItemProps) {
  return (
    <li
      className="cc-flex cc-transition-all sm:cc-px-4 cc-font-semibold cc-text-high_em cc-text-sm dark:cc-text-dark-text-white dark:hover:cc-bg-[#464646] hover:cc-bg-gray-25 cc-cursor-pointer"
      key={item}
    >
      <div className="cc-py-3 cc-border-b cc-border-outline-base_em cc-w-full">
        <div className="cc-gap-2 cc-flex cc-items-start cc-w-full sm:cc-px-2">
          <Checkbox
            checked={isChecked}
            className="cc-my-0.5"
            onCheckedChange={onSelect}
          />
          <div className="cc-flex cc-flex-grow cc-gap-x-4 cc-gap-y-1 cc-flex-wrap">
            <p className="cc-flex cc-flex-grow cc-flex-start">{item}</p>
          </div>
        </div>
      </div>
    </li>
  );
}
